import React from 'react';

import { GroupBase, ValueContainerProps, components } from 'react-select';

import { SelectVariant } from '../../Select';
import styles from './LimitedMultiValueContainer.module.scss';

interface SelectPropsWithCustomProps<Option, Group extends GroupBase<Option>> {
    maxVisibleOptions?: number;
    variant?: SelectVariant;
    options?: readonly (Option | Group)[];
}

/**
 * A ValueContainer that limits the number of visible selected options
 * and shows a "+X" indicator for the remaining options.
 * When all options are selected, shows "All" instead of individual chips.
 *
 * Pass `maxVisibleOptions` prop to the Select to control how many chips are shown.
 * When used with `variant="filter"`, the "+X" indicator inherits filter variant styling.
 *
 * @example
 * ```tsx
 * <Select
 *     isMulti
 *     maxVisibleOptions={2}
 *     variant="filter"
 *     components={{
 *         ValueContainer: LimitedMultiValueContainer,
 *     }}
 *     // ... other props
 * />
 * ```
 */
export const LimitedMultiValueContainer = <Option, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: ValueContainerProps<Option, true, Group>,
) => {
    const { children, getValue, selectProps, options } = props;
    const { maxVisibleOptions = 2, variant = 'default' } = selectProps as unknown as SelectPropsWithCustomProps<
        Option,
        Group
    >;

    const values = getValue();
    const childrenArray = React.Children.toArray(children);

    // Separate MultiValue components from other children (Input, Placeholder, etc.)
    const multiValueChildren: React.ReactNode[] = [];
    const otherChildren: React.ReactNode[] = [];

    childrenArray.forEach((child) => {
        if (React.isValidElement(child) && child.type === components.MultiValue) {
            multiValueChildren.push(child);
        } else {
            otherChildren.push(child);
        }
    });

    const totalSelected = values.length;
    const totalOptions = options.length;
    const isAllSelected = totalSelected === totalOptions && totalOptions > 0;
    const hiddenCount = totalSelected - maxVisibleOptions;

    // Determine if filter variant styling should be applied
    const isFilterActive = variant === 'filter' && totalSelected > 0;
    const indicatorClass = isFilterActive ? styles.moreIndicatorFilter : styles.moreIndicator;

    // Get visible MultiValue components
    const visibleMultiValues = multiValueChildren.slice(0, maxVisibleOptions);

    // Render content based on selection state
    const renderContent = () => {
        if (isAllSelected) {
            return <div className={indicatorClass}>All</div>;
        }
        return (
            <>
                {visibleMultiValues}
                {hiddenCount > 0 && <div className={indicatorClass}>+{hiddenCount}</div>}
            </>
        );
    };

    return (
        <components.ValueContainer {...props}>
            {renderContent()}
            {otherChildren}
        </components.ValueContainer>
    );
};
